import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  DialedDigitExtractionServer,
  DialedDigitExtractionServerPrivileges,
} from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.types';
import * as networkSelectors from 'data/network/network.selectors';
import * as networkActions from 'data/network/network.actions';
import * as actions from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.selectors';
import ModalDialog from 'shared/modal/dialog';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import { adaptGridData } from 'routes/DialedDigitExtractionServer/utils/dialedDigitExtractionServerTemplate.adapter';
import DistributorGrid from './components/DialedDigitExtractionServerGrid';
import { DistributorMenuActionsType, distributorColumnDefs } from '../../DialedDigitExtractionServer.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getDialedDigitExtractionServerList(state),
  networkInterfaceList: networkSelectors.getNetworks(state),
  selectedDialedDigitExtractionServer: selectors.getSelectedDialedDigitExtractionServer(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToDialedDigitExtractionServerCreate: actions.goToDialedDigitExtractionServerCreate,
  goToDialedDigitExtractionServerEdit: actions.goToDialedDigitExtractionServerEdit,
  goToDialedDigitExtractionServerView: actions.goToDialedDigitExtractionServerView,
  selectDialedDigitExtractionServer: actions.selectDialedDigitExtractionServer,
  getDialedDigitExtractionServerList: actions.getDialedDigitExtractionServerList,
  deleteDialedDigitExtractionServer: actions.deleteDialedDigitExtractionServer,
  getNetworks: networkActions.getNetworks,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const DialedDigitExtractionServerList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  networkInterfaceList,
  selectedDialedDigitExtractionServer,
  getNetworks,
  goToDialedDigitExtractionServerCreate,
  goToDialedDigitExtractionServerEdit,
  goToDialedDigitExtractionServerView,
  selectDialedDigitExtractionServer,
  getDialedDigitExtractionServerList,
  deleteDialedDigitExtractionServer,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs, setColumnDefs] = useState<ColDef[]>(distributorColumnDefs);
  const [isDeletingDialedDigitExtractionServer, setIsDeletingDialedDigitExtractionServer] = useState(false);
  const [deleteDialogCustomComponent, setDeleteDialogCustomComponent] =
    useState<FunctionComponent>(() => null);
  const [deleteDialogTitle, setDeleteDialogTitle] = useState('');
  const [deleteDialogActionText, setDeleteDialogActionText] = useState('');
  const { t } = useTranslation();
  const [gridData, setGridData] = useState<DialedDigitExtractionServer[]>(data);

  useEffect(() => {
    getDialedDigitExtractionServerList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getDialedDigitExtractionServerList]);

  useEffect(() => {
    getNetworks(deliveryFunctionId);
  }, [deliveryFunctionId, getNetworks]);

  useEffect(() => {
    setGridData(adaptGridData(data, networkInterfaceList));
    setLastUpdatedAt(new Date());
  }, [data, networkInterfaceList]);

  const handleRefresh = () => getDialedDigitExtractionServerList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToDialedDigitExtractionServerCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectDialedDigitExtractionServer(event.api.getSelectedRows());
  };

  const handleSelectItemMenuAction = () => goToDialedDigitExtractionServerView(history);

  const handleEdit = () => goToDialedDigitExtractionServerEdit(history);

  const handleDeleteDialedDigitExtractionServer = () => {
    const distributorConfigrationId = selectedDialedDigitExtractionServer[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedDialedDigitExtractionServer[0]?.msid,
        AuditService.DIALED_DIGIT_EXTRACTION_SERVER,
        AuditActionType.DIALED_DIGIT_EXTRACTION_SERVER_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteDialedDigitExtractionServer(deliveryFunctionId, distributorConfigrationId, body);
  };

  const hasPrivilegeToView = privileges.includes(DialedDigitExtractionServerPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(DialedDigitExtractionServerPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(DialedDigitExtractionServerPrivileges.Delete);

  return (
    <Fragment>
      <DistributorGrid
        data={gridData}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        lastUpdatedAt={lastUpdatedAt}
        onDeleteRow={handleDeleteDialedDigitExtractionServer}
        onEdit={handleEdit}
        selectedDialedDigitExtractionServer={selectedDialedDigitExtractionServer[0]}
      />
    </Fragment>
  );
};

export default withRouter(connector(DialedDigitExtractionServerList));
